import { useWatch } from 'react-hook-form'
import { useFieldArray } from 'react-hook-form'
import { Control } from 'react-hook-form'
import { FormValues } from './types'
import { Input } from 'src/components/inputs/Input'
import { SelectInput } from 'src/components/inputs/SelectInput'
import { useTranslation } from 'react-i18next'
import { FieldArrayControls } from './FieldArrayControls'

export default function PathwayTaskInput({
  pIdx,
  prIdx,
  control,
  coachCategoryOptions,
  taskCategoryOptions,
  taskFrequencyOptions,
}: {
  pIdx: number
  prIdx: number
  control: Control<FormValues>
  coachCategoryOptions: Record<string, any>[]
  taskCategoryOptions: Record<string, string>[]
  taskFrequencyOptions: Record<string, string>[]
}) {
  const { t } = useTranslation()
  const tasksFieldArray = useFieldArray({
    control,
    name: `pathways.${pIdx}.programs.${prIdx}.tasks` as const,
  })

  const rowTaskValues = useWatch({
    control,
    name: `pathways.${pIdx}.programs.${prIdx}.tasks` as const,
  })

  return (
    <>
      {tasksFieldArray.fields.map((task, tIdx) => (
        <div
          key={task.id}
          className="card mb-2 p-3 position-relative"
          style={{
            background: '#fff',
            border: '1px solid #e0e6ed',
            borderRadius: 10,
            boxShadow: '0 1px 4px 0 rgba(80, 80, 120, 0.03)',
          }}
        >
          <div className="row align-items-center">
            <div className="col-md-6 col-12">
              <h6 style={{ fontWeight: 500, color: '#3b4a6b' }}>
                Task {tIdx + 1}
              </h6>
            </div>
            <div className="col-md-6 col-12 d-flex justify-content-end gap-2">
              <FieldArrayControls
                fieldArray={tasksFieldArray}
                index={tIdx}
                onClone={() => {
                  const taskToClone = rowTaskValues[tIdx]
                  tasksFieldArray.insert(
                    tIdx + 1,
                    JSON.parse(JSON.stringify(taskToClone)),
                  )
                }}
              />
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 col-12">
              <Input
                required
                label={t('resource.pathway.field.taskTitle')}
                source={`pathways.${pIdx}.programs.${prIdx}.tasks.${tIdx}.title`}
              />
            </div>
            <div className="col-md-6 col-12">
              <Input
                label={t('resource.pathway.field.taskDescription')}
                source={`pathways.${pIdx}.programs.${prIdx}.tasks.${tIdx}.description`}
              />
            </div>
            <div className="col-md-6 col-12 mt-2">
              <SelectInput
                required
                label={t('createTaskModal.form.coachCategory')}
                options={coachCategoryOptions}
                source={`pathways.${pIdx}.programs.${prIdx}.tasks.${tIdx}.coach_category`}
              />
            </div>
            <div className="col-md-2 col-12 mt-2">
              <SelectInput
                required
                label={t('resource.pathway.field.taskCategory')}
                options={taskCategoryOptions}
                source={`pathways.${pIdx}.programs.${prIdx}.tasks.${tIdx}.task_type`}
              />
            </div>
            <div className="col-md-2 col-12 mt-2">
              <SelectInput
                required
                label={t('resource.pathway.field.taskFrequency')}
                options={taskFrequencyOptions}
                source={`pathways.${pIdx}.programs.${prIdx}.tasks.${tIdx}.frequency_unit`}
              />
            </div>
            <div className="col-md-2 col-12 mt-2">
              <Input
                required
                source={`pathways.${pIdx}.programs.${prIdx}.tasks.${tIdx}.frequency_repeats`}
                label={t(
                  'resource.pathway.field.taskFrequencyRepeats',
                  'Repeats',
                )}
                validate={(value) => {
                  if (!value && Number(value) !== 0) {
                    return t('error_messages.isRequired')
                  }
                  if (Number(value) > 30) {
                    return t('error_messages.valueBetween', {
                      value1: 0,
                      value2: 30,
                    })
                  }
                }}
              />
            </div>
          </div>
        </div>
      ))}
      <div style={{ maxWidth: '100px' }}>
        <button
          className="btn btn-primary w-100 mt-2"
          disabled={rowTaskValues.some(
            (task) =>
              !task.title || !task.coach_category || task.title.trim() === '',
          )}
          onClick={() =>
            tasksFieldArray.append({
              title: '',
              description: '',
              coach_category: '',
              due_date: '',
              task_type: '',
              frequency_unit: 'once',
              frequency_repeats: 0,
            })
          }
        >
          Add Task
        </button>
      </div>
    </>
  )
}
